import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { getAuthHeaders, getApiUrl } from '../utils/apiConfig';
import {
  getActingOrganizationId,
  setActingOrganizationId,
} from '../utils/actingOrganization';

/**
 * The organizations the signed-in person belongs to, and which one they are
 * working in.
 *
 * Most people have exactly one, and for them nothing changes: no switcher is
 * shown and no header is sent. Someone who belongs to several — a chain's
 * regional manager, a buying group operating its members' sites — picks one,
 * and every call from then on carries `X-Organization-Id`. The API validates
 * that against their membership, and their role and store locations come from
 * the membership in that organization.
 *
 * Someone who manages a buying group or a head office also gets the member
 * stores that accepted its invitation, as `via: 'group'`; `groups` names the
 * groups themselves and is what tells the shell to show the group pages.
 *
 * Admin impersonation is untouched: it is a separate context and wins on the
 * server, so the switcher stays out of its way.
 */
export interface SessionOrganization {
  id: string;
  name: string;
  type: string | null;
  status: string | null;
  role: string;
  locationIds: string[];
  isPrimary: boolean;
  /** How this person reaches it: their own membership, or a group they manage. */
  via?: 'member' | 'group';
  groupId?: string;
  groupName?: string;
}

/** A managed group: a buying group, or a multi-store owner's head office. */
export interface SessionGroup {
  id: string;
  name: string;
  kind: 'buying_group' | 'multi_store';
  memberCount: number;
}

interface OrganizationContextValue {
  organizations: SessionOrganization[];
  /** The groups this person manages; empty for everyone else. */
  groups: SessionGroup[];
  /** True when the group pages belong in this person's shell. */
  managesGroup: boolean;
  /** The organization the shell is working in, or `null` while unknown. */
  actingOrganization: SessionOrganization | null;
  actingOrganizationId: string | null;
  /** True only when there is something to switch between. */
  canSwitch: boolean;
  switchOrganization: (organizationId: string) => void;
  isLoading: boolean;
}

const OrganizationContext = createContext<OrganizationContextValue | undefined>(undefined);

export const useOrganizations = (): OrganizationContextValue => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganizations must be used within an OrganizationProvider');
  }
  return context;
};

interface SessionOrganizationsResponse {
  organizations: SessionOrganization[];
  groups?: SessionGroup[];
  primaryOrganizationId: string | null;
  actingOrganizationId: string | null;
  /** The `organization_switching` flag, on for the primary org or any listed membership. */
  switchingEnabled: boolean;
}

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<SessionOrganizationsResponse>({
    queryKey: ['session-organizations', currentUser?.uid],
    queryFn: async () => {
      const response = await fetch(`${getApiUrl()}/session/organizations`, {
        headers: await getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Failed to load organizations');
      }
      const body = await response.json();
      return body?.data as SessionOrganizationsResponse;
    },
    enabled: Boolean(currentUser?.uid && currentUser.emailVerified),
    staleTime: 5 * 60 * 1000,
    // One membership is the normal case; a retry storm helps nobody.
    retry: false,
  });

  const organizations = useMemo(() => data?.organizations ?? [], [data]);
  const uid = currentUser?.uid;
  // Default true while the list is still loading, so nothing flashes on;
  // once loaded, a stated `false` is authoritative.
  const switchingEnabled = data?.switchingEnabled !== false;

  // A stored choice for an organization this person no longer belongs to must
  // be dropped from storage: getActingOrganizationHeaders still reads it, and
  // the API would refuse every subsequent call (including another list fetch).
  // The same is true when the switching flag is off — but only for a direct
  // membership: a group-granted store (`via: 'group'`) switches regardless of
  // `organization_switching`, so a stored choice pointing at one must survive.
  useEffect(() => {
    if (!data || !uid) return;
    const chosen = getActingOrganizationId(uid);
    if (!chosen) return;
    const match = organizations.find((org) => org.id === chosen);
    const shouldClear = !match || (!switchingEnabled && match.via !== 'group');
    if (shouldClear) {
      setActingOrganizationId(uid, null);
    }
  }, [data, organizations, switchingEnabled, uid]);

  const groups = useMemo(() => data?.groups ?? [], [data]);

  const actingOrganizationId = useMemo(() => {
    const chosen = getActingOrganizationId(uid);
    if (chosen) {
      const match = organizations.find((org) => org.id === chosen);
      if (match && (switchingEnabled || match.via === 'group')) return chosen;
    }
    return data?.actingOrganizationId ?? data?.primaryOrganizationId ?? null;
  }, [data, organizations, switchingEnabled, uid]);

  const switchOrganization = useCallback(
    (organizationId: string) => {
      if (!uid || organizationId === getActingOrganizationId(uid)) return;
      setActingOrganizationId(uid, organizationId);
      // Everything cached was fetched for the organization we are leaving, and
      // a request already in flight carries its header — cancel, then clear,
      // or it lands afterwards and refills what was just dropped.
      queryClient.cancelQueries();
      queryClient.clear();
    },
    [queryClient, uid],
  );

  // Switching is on the table when there is more than one direct membership
  // and the flag allows it, or when there is at least one group-granted store
  // — a group entry is switchable regardless of `organization_switching`.
  const directMembershipCount = useMemo(
    () => organizations.filter((org) => org.via !== 'group').length,
    [organizations],
  );
  const canSwitch =
    (switchingEnabled && directMembershipCount > 1) || groups.length > 0;

  const value = useMemo<OrganizationContextValue>(
    () => ({
      organizations,
      groups,
      managesGroup: groups.length > 0,
      actingOrganizationId,
      actingOrganization: organizations.find((org) => org.id === actingOrganizationId) ?? null,
      canSwitch,
      switchOrganization,
      isLoading,
    }),
    [organizations, groups, actingOrganizationId, canSwitch, switchOrganization, isLoading],
  );

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};
